(function () {
  'use strict';

  /**
   * why-us-reviews.js
   *
   * Fetches Google Places reviews via the Cloudflare Worker proxy
   * (configured in tab-content-why-us.liquid via data-proxy-url).
   *
   * The proxy returns a normalised JSON shape:
   * {
   *   rating: 4.9,                    // overall rating (number)
   *   user_ratings_total: 247,        // total review count (integer)
   *   reviews: [                      // array of review objects
   *     {
   *       author_name: "John D.",
   *       profile_photo_url: "https://...",  // may be empty
   *       rating: 5,                          // integer 1-5
   *       relative_time_description: "2 weeks ago",
   *       text: "Great service..."
   *     }
   *   ]
   * }
   *
   * Cache strategy: store the last successful response in
   * sessionStorage so repeated PDP views within one session don't
   * re-hit the proxy. The proxy itself caches at the edge to
   * minimise Google API calls.
   */

  var CACHE_KEY = 'justtilesni_google_reviews_v1';
  var CACHE_TTL_MS = 6 * 60 * 60 * 1000; // 6 hours

  function init() {
    var container = document.querySelector('[data-google-reviews]');
    if (!container) return;

    var proxyUrl  = container.dataset.proxyUrl;
    var placeId   = container.dataset.placeId;
    var reviewsUrl = container.dataset.reviewsUrl;

    if (!proxyUrl || !placeId) {
      showFallback(container);
      return;
    }

    // Try cache first
    var cached = readCache();
    if (cached) {
      render(container, cached, reviewsUrl);
      return;
    }

    // Fetch from proxy
    var url = proxyUrl + (proxyUrl.indexOf('?') === -1 ? '?' : '&') +
              'place_id=' + encodeURIComponent(placeId);

    fetch(url, { method: 'GET', credentials: 'omit' })
      .then(function (res) {
        if (!res.ok) throw new Error('Proxy responded ' + res.status);
        return res.json();
      })
      .then(function (data) {
        if (!data || typeof data.rating === 'undefined') {
          throw new Error('Malformed proxy response');
        }
        writeCache(data);
        render(container, data, reviewsUrl);
      })
      .catch(function (err) {
        console.warn('[why-us-reviews] Failed to load:', err);
        showFallback(container);
      });
  }

  function readCache() {
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed.timestamp || (Date.now() - parsed.timestamp) > CACHE_TTL_MS) {
        return null;
      }
      return parsed.data;
    } catch (e) {
      return null;
    }
  }

  function writeCache(data) {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({
        timestamp: Date.now(),
        data: data
      }));
    } catch (e) {
      // Storage may be full or disabled - non-fatal
    }
  }

  function render(container, data, reviewsUrl) {
    renderBadge(container, data);
    renderFeed(container, data.reviews || [], reviewsUrl);
  }

  function renderBadge(container, data) {
    var ratingEl = container.querySelector('[data-rating]');
    var starsEl  = container.querySelector('[data-stars]');
    var countEl  = container.querySelector('[data-review-count]');

    var rating = parseFloat(data.rating || 0);
    var count  = parseInt(data.user_ratings_total || 0, 10);

    if (ratingEl) ratingEl.textContent = rating.toFixed(1);
    if (starsEl)  starsEl.innerHTML = buildStars(rating);
    if (countEl) {
      countEl.textContent = count > 0
        ? 'Based on ' + count.toLocaleString() + ' Google reviews'
        : 'Reviews from our Google Business profile';
    }
  }

  function renderFeed(container, reviews, reviewsUrl) {
    var feed = container.querySelector('[data-reviews-feed]');
    if (!feed) return;

    if (!reviews.length) {
      feed.innerHTML = '<div class="why-us-reviews__loading">No reviews to display yet.</div>';
      return;
    }

    // Sort by rating desc then newest first (Google's order is OK to keep)
    var frag = document.createDocumentFragment();
    reviews.slice(0, 8).forEach(function (r) {
      frag.appendChild(buildReviewCard(r));
    });
    feed.innerHTML = '';
    feed.appendChild(frag);
  }

  function buildReviewCard(review) {
    var card = document.createElement('div');
    card.className = 'why-us-review';

    var head = document.createElement('div');
    head.className = 'why-us-review__head';

    var avatar = document.createElement('div');
    avatar.className = 'why-us-review__avatar';
    if (review.profile_photo_url) {
      avatar.style.backgroundImage = 'url(' + encodeURI(review.profile_photo_url) + ')';
    } else {
      avatar.textContent = (review.author_name || '?').charAt(0).toUpperCase();
    }

    var authorBlock = document.createElement('div');
    authorBlock.className = 'why-us-review__author-block';

    var author = document.createElement('span');
    author.className = 'why-us-review__author';
    author.textContent = review.author_name || 'Anonymous';

    var meta = document.createElement('div');
    meta.className = 'why-us-review__meta';

    var stars = document.createElement('span');
    stars.className = 'why-us-review__stars';
    stars.setAttribute('aria-hidden', 'true');
    stars.innerHTML = buildStars(review.rating || 0);

    var date = document.createElement('span');
    date.className = 'why-us-review__date';
    date.textContent = review.relative_time_description || '';

    meta.appendChild(stars);
    if (date.textContent) meta.appendChild(date);

    authorBlock.appendChild(author);
    authorBlock.appendChild(meta);

    head.appendChild(avatar);
    head.appendChild(authorBlock);
    card.appendChild(head);

    var text = document.createElement('p');
    text.className = 'why-us-review__text';
    text.textContent = (review.text || '').trim();
    card.appendChild(text);

    // Add expand toggle if text looks long
    if ((review.text || '').length > 280) {
      var expand = document.createElement('button');
      expand.type = 'button';
      expand.className = 'why-us-review__expand';
      expand.textContent = 'Read more';
      expand.addEventListener('click', function () {
        var expanded = card.classList.toggle('why-us-review--expanded');
        expand.textContent = expanded ? 'Show less' : 'Read more';
      });
      card.appendChild(expand);
    }

    return card;
  }

  function buildStars(rating) {
    var r = Math.max(0, Math.min(5, parseFloat(rating) || 0));
    var full = Math.floor(r);
    var hasHalf = (r - full) >= 0.3 && (r - full) <= 0.7;
    if ((r - full) > 0.7) { full += 1; }
    var empty = 5 - full - (hasHalf ? 1 : 0);

    var html = '';
    for (var i = 0; i < full; i++) html += '★';
    if (hasHalf) html += '<span style="opacity:0.5">★</span>';
    for (var j = 0; j < empty; j++) html += '<span style="opacity:0.25">★</span>';
    return html;
  }

  function showFallback(container) {
    var feed     = container.querySelector('[data-reviews-feed]');
    var fallback = container.querySelector('[data-reviews-fallback]');
    if (feed) feed.style.display = 'none';
    if (fallback) fallback.hidden = false;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
